// ─────────────────────────────────────────────
// Oike — «Recorrido del cliente» on a contact's card (pure; tested by scripts/test-client-path.mjs).
// What happened with one contact, from their request to the service, using only links that exist by id:
//   request  → Diagnostic Intake with this contact (Stakeholder) that came from the form (Token or Submitted)
//   quote    → a chain of versions (src/quoteChains.js) with a version of this contact; it came from a request only
//              when one of its versions IS that request (a form quote); otherwise «sin solicitud vinculada»
//   accepted → the accepted version (Won, Won At, Accepted Via)
//   charges  → Charges › Quote = a version of the chain; payments by their active Payment Applications
//   service  → Diagnostics › Service Origins / Coaching Processes › Service Origin with a version of the chain
//   opportunities → Opportunities › Contact or Origin Request of one of their requests (several contacts: ambiguous)
// Never by name, company or email. A link that is missing is said («falta»), never filled in, and a source that
// could not be read is «no disponible», never empty. Nothing is written: the screen only reads and opens records.
// ─────────────────────────────────────────────
import { chainsOf } from './quoteChains.js';
import { isAccepted, isPublished } from './quoteRules.js';
import { ACCEPTED_VIA } from './quoteFile.js';

const REC_RE = /^rec[A-Za-z0-9]{14}$/;
const links = (v) => (Array.isArray(v) ? v.map(x => (typeof x === 'string' ? x : x?.id)).filter(Boolean) : []);
const name = (v) => (v && typeof v === 'object' && !Array.isArray(v) ? (v.name ?? '') : (v ?? ''));
const day = (v) => (v ? String(v).slice(0, 10) : '');
const CLOSED = /won|lost|ganad|perdid|cerrad|closed/i;

/** A request is a record of the public form or of an identified link: it has its Token or was submitted. */
export const isRequestRecord = (f = {}) => !!(f['Token'] || f['Status'] === 'Submitted');

/** The requests of this contact, by id (newest first). */
export function requestsOf(intakes = [], stakeholderId) {
  if (!REC_RE.test(String(stakeholderId || ''))) return [];
  return intakes
    .filter(r => links(r.fields?.['Stakeholder']).includes(stakeholderId) && isRequestRecord(r.fields || {}))
    .map(r => ({ id: r.id, name: String(r.fields?.['Name'] || 'Solicitud'), day: day(r.fields?.['Submitted At'] || r.createdTime) }))
    .sort((a, b) => b.day.localeCompare(a.day));
}

/**
 * The opportunities of this contact, the same rule as the coach's card (shared/coachingContacts.ts › commercialOf):
 * by Opportunities › Contact, or by Origin Request of one of their requests when no other contact is linked.
 * With several contacts, or their request with another contact: ambiguous, never attributed to them.
 */
export function opportunitiesOf(opportunities = [], stakeholderId, requestIds = []) {
  const reqs = new Set(requestIds);
  const personal = [], ambiguous = [];
  for (const o of opportunities) {
    const contacts = links(o.fields?.['Contact']);
    const byContact = contacts.includes(stakeholderId);
    const byRequest = links(o.fields?.['Origin Request']).some(r => reqs.has(r));
    if (!byContact && !byRequest) continue;
    const stage = name(o.fields?.['Stage']) || '';
    const view = { id: o.id, name: String(o.fields?.['Deal/Opp name'] || 'Oportunidad'), stage, closed: CLOSED.test(stage), via: byContact ? 'contact' : 'request', accountId: links(o.fields?.['Account'])[0] || '' };
    if ((byContact && contacts.length > 1) || (!byContact && contacts.length > 0)) ambiguous.push(view);
    else personal.push(view);
  }
  return { personal, ambiguous };
}

/** The chains of quotes with a version of this contact (by id), as in ContactQuotes. */
export function contractsOf(intakes = [], stakeholderId) {
  if (!REC_RE.test(String(stakeholderId || ''))) return [];
  return chainsOf(intakes).filter(c => c.versions.some(v => links(v.record.fields?.['Stakeholder']).includes(stakeholderId)));
}

/**
 * The charges and payments of a chain, from the read-only list of charges.mts (`billing`: { charges, payments,
 * applications }). `billing` null means it could not be read or this login cannot see payments: then `null`
 * (unknown), never an empty list.
 */
export function moneyOfChain(chain, billing) {
  if (!billing) return null;
  const ids = new Set(chain.versions.map(v => v.id));
  const charges = (billing.charges || []).filter(c => links(c.fields?.['Quote']).some(q => ids.has(q)));
  const chargeIds = new Set(charges.map(c => c.id));
  const apps = (billing.applications || []).filter(a => name(a.fields?.['Application Status']) === 'active' && links(a.fields?.['Charge']).some(c => chargeIds.has(c)));
  const payIds = new Set(apps.map(a => links(a.fields?.['Payment'])[0]).filter(Boolean));
  const payments = (billing.payments || []).filter(p => payIds.has(p.id) && name(p.fields?.['Payment Status']) === 'confirmed')
    .map(p => ({ id: p.id, day: day(p.fields?.['Received On']), amount: Number(p.fields?.['Amount']) || 0, currency: name(p.fields?.['Currency']) || '', refund: name(p.fields?.['Entry Type']) === 'refund', receipt: p.document?.number || '' }))
    .sort((a, b) => a.day.localeCompare(b.day));
  const open = charges.filter(c => name(c.fields?.['Charge Status']) !== 'cancelled');
  const pendingBy = {};
  for (const c of open) { const cur = c.state?.currency || name(c.fields?.['Currency']) || ''; pendingBy[cur] = Math.round(((pendingBy[cur] || 0) + (Number(c.state?.pending) || 0)) * 100) / 100; }
  return { charges: open.length, cancelled: charges.length - open.length, firstChargeId: open[0]?.id || charges[0]?.id || '', payments, pendingBy };
}

/**
 * The services started from a chain, from the read-only answers of «Preparar inicio del servicio»
 * (`services`: { diagnostics, processes } with their origins, or { error }). Only origins whose quote is a version
 * of this chain: a process of the same company or with the same people is not this contract's service.
 */
export function servicesOfChain(chain, services) {
  if (!services || services.error) return null;
  const ids = new Set(chain.versions.map(v => v.id));
  const out = [];
  for (const d of services.diagnostics || []) for (const o of d.origins || []) if (ids.has(o.quoteId)) out.push({ kind: 'diagnostic', id: d.id, name: d.name || 'Diagnóstico', service: o.service || '', day: day(o.at), accountId: services.accountId || '' });
  for (const p of services.processes || []) for (const o of p.origins || []) if (ids.has(o.quoteId)) out.push({ kind: 'coaching', id: p.id, name: p.name || 'Acompañamiento', service: o.service || '', day: day(o.at) });
  const seen = new Set();
  return out.filter(s => { const k = `${s.kind}:${s.id}`; if (seen.has(k)) return false; seen.add(k); return true; });
}

/**
 * The steps of one contract, each { key, state: done | missing | unknown | pending, text, day, open }.
 * `landings`: Consulting Landing records already loaded (for the publication day only).
 */
export function stepsOfChain(chain, { landings = [], money = undefined, services = undefined } = {}) {
  const head = chain.operative || chain.current || chain.versions[chain.versions.length - 1];
  const sale = chain.sale;
  const requestVersion = chain.versions.find(v => isRequestRecord(v.record.fields || {}));
  const steps = [];
  steps.push(requestVersion
    ? { key: 'request', state: 'done', text: `Solicitud: ${requestVersion.record.fields?.['Name'] || 'Solicitud'}`, day: day(requestVersion.record.fields?.['Submitted At'] || requestVersion.record.createdTime), open: { page: 'quotes', quoteId: requestVersion.id } }
    : { key: 'request', state: 'missing', text: 'Sin solicitud vinculada: la cotización no nació de un formulario de esta persona.' });
  const versionsText = chain.versions.length > 1 ? ` · ${chain.versions.length} versiones` : '';
  // The day a record was created is shown only for a quote born apart from a request (a form quote is the request itself)
  steps.push({ key: 'quote', state: 'done', text: `Cotización: ${head.record.fields?.['Name'] || 'Cotización'}${versionsText}`, day: requestVersion ? '' : day(head.record.createdTime), open: { page: 'quotes', quoteId: head.id } });
  const published = chain.versions.filter(v => isPublished(v.record.fields || {}));
  if (published.length) {
    const last = published[published.length - 1];
    const landing = landings.find(l => l.id === links(last.record.fields?.['Consulting Landing'])[0]);
    steps.push({ key: 'published', state: 'done', text: 'Publicada (publicar no es enviar)', day: day(landing?.fields?.['Published At']), open: { page: 'quotes', quoteId: last.id } });
  } else steps.push({ key: 'published', state: 'pending', text: 'Sin publicar todavía.' });
  if (sale && isAccepted(sale.record.fields || {})) {
    const via = ACCEPTED_VIA[name(sale.record.fields?.['Accepted Via'])] || '';
    const won = day(sale.record.fields?.['Won At']);
    steps.push({ key: 'accepted', state: 'done', text: `Aceptada${via ? ` · ${via.toLowerCase()}` : ''}${won ? '' : ' · sin fecha registrada'}`, day: won, open: { page: 'quotes', quoteId: sale.id } });
  } else if (chain.versions.some(v => v.state === 'cancelled')) {
    steps.push({ key: 'accepted', state: 'missing', text: 'Aceptación cancelada por el cliente.' });
    return steps;
  } else {
    steps.push({ key: 'accepted', state: 'pending', text: chain.versions.some(v => v.state === 'lost') ? 'No aceptada.' : 'Sin aceptación registrada.' });
    return steps;
  }
  // Charges and payments: only after an acceptance, from the charges of its versions
  if (money === undefined) steps.push({ key: 'charges', state: 'unknown', text: 'Cuotas y pagos: abre el recorrido para leerlos.' });
  else if (money === null) steps.push({ key: 'charges', state: 'unknown', text: 'Cuotas y pagos no disponibles: no se pudieron leer o tu perfil no los ve.' });
  else {
    const legacy = chain.payments.length > 0;
    steps.push(money.charges
      ? { key: 'charges', state: 'done', text: `${money.charges} cuota(s) registradas${Object.entries(money.pendingBy).filter(([, v]) => v > 0).length ? ` · saldo ${Object.entries(money.pendingBy).filter(([, v]) => v > 0).map(([c, v]) => `${c} ${v.toLocaleString('es')}`).join(' · ')}` : ' · sin saldo'}`, open: { page: 'quotes', quoteId: sale.id } }
      : { key: 'charges', state: 'missing', text: `Sin cuotas registradas para esta contratación.${legacy ? ' Hay un estado de pago anterior en la cotización (historial, no es un pago).' : ''}`, open: { page: 'quotes', quoteId: sale.id } });
    if (money.payments.length) for (const p of money.payments) steps.push({ key: 'payment', state: 'done', text: `${p.refund ? 'Devolución' : 'Pago'} ${p.currency} ${p.amount.toLocaleString('es')}${p.receipt ? ` · ${p.refund ? 'comprobante' : 'recibo'} ${p.receipt}` : p.refund ? '' : ' · recibo pendiente de emisión'}`, day: p.day, open: { page: 'quotes', quoteId: sale.id } });
    else if (money.charges) steps.push({ key: 'payment', state: 'pending', text: 'Sin pagos registrados todavía.' });
  }
  if (services === undefined) steps.push({ key: 'service', state: 'unknown', text: 'Servicio: abre el recorrido para leerlo.' });
  else if (services === null) steps.push({ key: 'service', state: 'unknown', text: 'Servicio no disponible: no se pudo leer o tu perfil no lo ve.' });
  else if (!services.length) steps.push({ key: 'service', state: 'missing', text: 'Sin servicio vinculado a esta contratación (se prepara desde la ficha de la cotización).', open: { page: 'quotes', quoteId: sale.id } });
  else for (const s of services) steps.push({ key: 'service', state: 'done', text: `${s.kind === 'coaching' ? 'Acompañamiento' : 'Diagnóstico'}: ${s.name}${s.service ? ` · ${s.service}` : ''}`, day: s.day, open: s.kind === 'coaching' ? { page: 'coaching', processId: s.id } : { page: 'accounts', accountId: s.accountId, tab: 'diagnostic', diagnosticId: s.id } });
  return steps;
}

/** Everything the card shows for one contact. */
export function clientPath({ stakeholderId, intakes = [], opportunities = [], landings = [], billing = undefined, servicesByChain = {} }) {
  const requests = requestsOf(intakes, stakeholderId);
  const contracts = contractsOf(intakes, stakeholderId).map(chain => {
    const money = billing === undefined ? undefined : moneyOfChain(chain, billing);
    const svc = chain.sale ? (chain.id in servicesByChain ? servicesOfChain(chain, servicesByChain[chain.id]) : undefined) : undefined;
    return { id: chain.id, saleId: chain.sale?.id || '', title: String((chain.operative || chain.current || chain.versions[chain.versions.length - 1]).record.fields?.['Name'] || 'Cotización'), steps: stepsOfChain(chain, { landings, money, services: svc }) };
  });
  const quoted = new Set(contractsOf(intakes, stakeholderId).flatMap(c => c.versions.map(v => v.id)));
  return {
    requestsWithoutQuote: requests.filter(r => !quoted.has(r.id)),
    contracts,
    opportunities: opportunitiesOf(opportunities, stakeholderId, requests.map(r => r.id)),
  };
}
